import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { publicApi, authenticatedApi, supplierApi } from '../services/api';
import { handleUnauthorized } from '../config/api';

// Query keys
export const queryKeys = {
  // Public queries
  roles: ['roles'],
  rolesByUserType: (userType) => ['roles', userType],
  servicesOffered: ['servicesOffered'],
  engineerServices: ['engineerServices'],
  locations: ['locations'],
  questionnaireFormQuestions: ['questionnaireFormQuestions'],
  propertyAdminUser: (userId) => ['propertyAdminUser', userId],

  // Authenticated queries
  contractors: (page = 1, pageSize = 10, status = null) => ['contractors', page, pageSize, status],
  customers: (page = 1, pageSize = 10, status = null) => ['customers', page, pageSize, status],
  allUsers: (page = 1, pageSize = 10) => ['allUsers', page, pageSize],
  contractorDetails: (id) => ['contractorDetails', id],
  customerDetails: (id) => ['customerDetails', id],
  quotationOverview: (id) => ['quotationOverview', id],
  quotationDetail: (id) => ['quotationDetail', id],
  quotationTimeline: (quotationId) => ['quotationTimeline', quotationId],
  serviceProviderQuotations: (serviceProviderId) => ['serviceProviderQuotations', serviceProviderId],
  serviceProviderQuotationOverview: (quotationId) => ['serviceProviderQuotationOverview', quotationId],
  customerQuotations: (customerId) => ['customerQuotations', customerId],
  allQuotations: () => ['allQuotations'],
  // Authenticated dashboard queries
  dashboardStats: ['dashboard', 'stats'],
  quotations: ['quotations'],
  services: ['services'],
  userProfile: ['userProfile'],
  interestedActiveAds: (pageNumber, pageSize, sortBy, roleTypeFilter, serviceTypeFilter, locationFilter, language, filterQuotationStatus) => [
    'interestedActiveAds',
    pageNumber,
    pageSize,
    sortBy,
    roleTypeFilter,
    serviceTypeFilter,
    locationFilter,
    language,
    filterQuotationStatus,
  ],
  publicAds: (pageNumber = 1, pageSize = 10, roleTypeIdsKey = null, locationId = null, language = null, sortBy = null, isEnrolled = null, serviceIdsKey = null) => [
    'publicAds',
    pageNumber,
    pageSize,
    roleTypeIdsKey,
    locationId,
    language,
    sortBy,
    isEnrolled,
    serviceIdsKey,
  ],
  // Notification queries
  notifications: ['notifications'],
  unreadCount: ['unreadCount'],
  // Offers queries
  offers: (searchTerm, filter, page, pageSize, language) => ['offers', searchTerm, filter, page, pageSize, language],
  // Material Master queries
  materialHierarchy: ['materialHierarchy'],
  materialHierarchyPublic: ['materialHierarchyPublic'],
  // Admin posts list
  adminPosts: (pageNumber, pageSize, roleTypeId, locationId, language = null, sortBy = null, filterStatus = null) => ['adminPosts', pageNumber, pageSize, roleTypeId, locationId, language, sortBy, filterStatus],
  postOverview: (postId, language = null) => ['postOverview', postId, language],
  adminPostOverview: (postId) => ['adminPostOverview', postId],
};

// Public API hooks (no authentication required)
export const useRoles = (options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.roles, options.language],
    queryFn: () => publicApi.getRoles(options.language),
    // Roles rarely change; keep in memory and avoid refetch loops
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};



export const useServicesOffered = (options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.servicesOffered, options.language],
    queryFn: () => publicApi.getServicesOffered(options.language),
    staleTime: 10 * 60 * 1000, // 10 minutes - services don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    ...options, // Allow passing additional options like enabled
  });
};

export const useLocations = (options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.locations, options.language],
    queryFn: () => publicApi.getLocations(options.language),
    staleTime: 10 * 60 * 1000, // 10 minutes - locations don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    ...options, // Allow passing additional options like enabled
  });
};

/** Public materials hierarchy (no auth) - for supplier signup */
export const useMaterialHierarchyPublic = (options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.materialHierarchyPublic, options.language],
    queryFn: async () => {
      const response = await publicApi.getMaterialHierarchy(options.language);
      return response?.result ?? response;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};

/** Public engineer services - for engineer signup (Inspection service offering). v2 API returns result: [{ category, services }]. */
export const useEngineerServices = (options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.engineerServices, options.language],
    queryFn: async () => {
      const response = await publicApi.getEngineerServices(options.language);
      const result = response?.result ?? response;
      if (Array.isArray(result) && result.length > 0 && result[0]?.services) {
        return result[0].services;
      }
      return result;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};

export const useQuestionnaireFormQuestions = (options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.questionnaireFormQuestions, options.language, options.propertyType],
    queryFn: () =>
      authenticatedApi.getQuestionnaireFormQuestions(options.language, options.propertyType),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: (options.enabled ?? true) && !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options,
  });
};


// Ads API hooks (authorized endpoint)
export const usePublicAds = (pageNumber = 1, pageSize = 10, options = {}) => {
  const roleTypeIds = Array.isArray(options.roleTypeIds)
    ? options.roleTypeIds.filter((v) => v !== null && v !== undefined)
    : (options.roleTypeId !== null && options.roleTypeId !== undefined ? [options.roleTypeId] : []);
  const locationId = options.locationId ?? null;
  const sortBy = options.sortBy ?? null;
  const isEnrolled = options.isEnrolled;
  const serviceIds = Array.isArray(options.serviceIds)
    ? options.serviceIds.filter((v) => v !== null && v !== undefined && v !== '')
    : (options.serviceId != null && options.serviceId !== '' ? [options.serviceId] : []);
  const roleTypeIdsKey = roleTypeIds.length > 0 ? roleTypeIds.join(',') : null;
  const serviceIdsKey = serviceIds.length > 0 ? serviceIds.join(',') : null;
  return useQuery({
    queryKey: queryKeys.publicAds(pageNumber, pageSize, roleTypeIdsKey, locationId, options.language, sortBy, isEnrolled, serviceIdsKey),
    queryFn: () =>
      authenticatedApi.getPublicAds(
        pageNumber,
        pageSize,
        { roleTypeIds, locationId, sortBy, isEnrolled, serviceIds: serviceIds.length > 0 ? serviceIds : undefined },
        options.language
      ),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useInterestedActiveAds = (pageNumber = 1, pageSize = 10, options = {}) => {
  const sortBy = options.sortBy ?? null;
  const roleTypeFilter = options.roleType_filter ?? options.roleTypeId ?? null;
  const serviceTypeFilter = options.serviceType_filter ?? options.serviceId ?? null;
  const locationFilter = options.location_filter ?? options.locationId ?? null;
  const language = options.language ?? null;
  const filterQuotationStatus = options.filterQuotationStatus ?? null;
  return useQuery({
    queryKey: queryKeys.interestedActiveAds(
      pageNumber,
      pageSize,
      sortBy,
      roleTypeFilter,
      serviceTypeFilter,
      locationFilter,
      language,
      filterQuotationStatus
    ),
    queryFn: () =>
      authenticatedApi.getInterestedActiveAds(
        pageNumber,
        pageSize,
        {
          sortBy,
          roleType_filter: roleTypeFilter,
          serviceType_filter: serviceTypeFilter,
          location_filter: locationFilter,
          filterQuotationStatus: filterQuotationStatus || undefined,
        },
        language
      ),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options,
  });
};

export const usePostOverview = (postId, options = {}) => {
  const language = options.language ?? null;
  return useQuery({
    queryKey: queryKeys.postOverview(postId, language),
    queryFn: () => authenticatedApi.getPostOverview(postId, language),
    enabled: !!postId && (options.enabled !== false),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    ...options,
  });
};

export const useAdminPostOverview = (postId, options = {}) => {
  const language = options.language ?? null;
  return useQuery({
    queryKey: queryKeys.adminPostOverview(postId),
    queryFn: () => authenticatedApi.getAdminPostOverview(postId, language),
    enabled: !!postId && (options.enabled !== false),
    staleTime: 0,
    gcTime: 0,
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options,
  });
};

export const useEnrollServiceProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (postId) => authenticatedApi.enrollServiceProvider(postId),
    onSuccess: async (_, postId) => {
      const postIdStr = postId != null ? String(postId) : null;
      if (!postIdStr) return;
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'postOverview' && String(query.queryKey[1]) === postIdStr,
      });
      queryClient.invalidateQueries({ queryKey: ['interestedActiveAds'] });
      await queryClient.refetchQueries({
        predicate: (query) =>
          query.queryKey[0] === 'postOverview' && String(query.queryKey[1]) === postIdStr,
      });
    },
  });
};

export const usePropertyAdminUser = (userId, options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.propertyAdminUser(userId), options.language],
    queryFn: () => authenticatedApi.getPropertyAdminUser(userId, options.language),
    staleTime: 0,
    gcTime: 0,
    enabled: !!userId && (options.enabled ?? true) && !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options,
  });
};

export const useAdminPosts = (pageNumber = 1, pageSize = 10, { roleTypeId, locationId, language, sortBy, filterStatus } = {}, options = {}) => {
  const lang = language ?? options.language;
  return useQuery({
    queryKey: queryKeys.adminPosts(pageNumber, pageSize, roleTypeId, locationId, lang, sortBy, filterStatus),
    queryFn: () => authenticatedApi.getAdminPosts(pageNumber, pageSize, roleTypeId, locationId, lang, sortBy, filterStatus),
    staleTime: 0,
    gcTime: 0,

    enabled: (options.enabled ?? true) && !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options,
  });
};

// Authenticated API hooks (require authentication)
export const useContractors = (page = 1, pageSize = 10, status = null, filters = {}, sortTypes = []) => {
  return useQuery({
    queryKey: [...queryKeys.contractors(page, pageSize, status), filters, sortTypes],
    queryFn: () => authenticatedApi.getContractors(page, pageSize, status, filters, sortTypes),
    staleTime: 1 * 60 * 1000, // 1 minute (reduced for more frequent updates)
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true, // Always refetch when component mounts
    enabled: !!localStorage.getItem('access_token'), // Only run if user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useServiceProviders = (page = 1, pageSize = 10, status = null, filters = {}, sortTypes = []) => {
  return useQuery({
    queryKey: ['serviceProviders', page, pageSize, status, filters, sortTypes],
    queryFn: () => authenticatedApi.getServiceProviders(page, pageSize, status, filters, sortTypes),
    staleTime: 1 * 60 * 1000, // 1 minute (reduced for more frequent updates)
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true, // Always refetch when component mounts
    enabled: !!localStorage.getItem('access_token'), // Only run if user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useServiceProvidersByLicense = (page = 1, pageSize = 10, statuses = []) => {
  return useQuery({
    queryKey: ['serviceProvidersByLicense', page, pageSize, statuses],
    queryFn: () => authenticatedApi.getServiceProvidersByLicense(page, pageSize, statuses),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true, // Always refetch when component mounts
    enabled: !!localStorage.getItem('access_token'), // Only run if user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useCustomers = (page = 1, pageSize = 10, status = null, filters = {}, sortTypes = []) => {
  return useQuery({
    queryKey: [...queryKeys.customers(page, pageSize, status), filters, sortTypes],
    queryFn: () => authenticatedApi.getCustomers(page, pageSize, status, filters, sortTypes),
    staleTime: 1 * 60 * 1000, // 1 minute (reduced for more frequent updates)
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true, // Always refetch when component mounts
    enabled: !!localStorage.getItem('access_token'), // Only run if user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useCustomersTotalCount = () => {
  return useQuery({
    queryKey: ['customers', 'totalCount'],
    queryFn: () => authenticatedApi.getCustomers(1, 1, null, {}, []), // Get just 1 item to get total count
    staleTime: 5 * 60 * 1000, // 5 minutes - total count doesn't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    enabled: !!localStorage.getItem('access_token'), // Only run if user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useAllUsers = (page = 1, pageSize = 10) => {
  return useQuery({
    queryKey: queryKeys.allUsers(page, pageSize),
    queryFn: () => authenticatedApi.getAllUsers(page, pageSize),
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    enabled: !!localStorage.getItem('access_token'), // Only run if user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useContractorDetails = (id) => {
  return useQuery({
    queryKey: queryKeys.contractorDetails(id),
    queryFn: () => authenticatedApi.getContractorDetails(id),
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    enabled: !!id && !!localStorage.getItem('access_token'), // Only run if ID is provided and user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useCustomerDetails = (id) => {
  return useQuery({
    queryKey: queryKeys.customerDetails(id),
    queryFn: () => authenticatedApi.getCustomerDetails(id),
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    enabled: !!id && !!localStorage.getItem('access_token'), // Only run if ID is provided and user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};



export const useServiceProviderQuotations = (serviceProviderId, page = 1, pageSize = 10, status = null, search = null, dateFrom = null, dateTo = null, type = null, sortTypes = []) => {
  return useQuery({
    queryKey: [...queryKeys.serviceProviderQuotations(serviceProviderId), page, pageSize, status, search, dateFrom, dateTo, type, sortTypes],
    queryFn: () => authenticatedApi.getServiceProviderQuotations(serviceProviderId, page, pageSize, status, search, dateFrom, dateTo, type, sortTypes),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!serviceProviderId && !!localStorage.getItem('access_token'), // Only run if ID is provided and user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useServiceProviderQuotationOverview = (quotationId) => {
  return useQuery({
    queryKey: queryKeys.serviceProviderQuotationOverview(quotationId),
    queryFn: () => authenticatedApi.getServiceProviderQuotationOverview(quotationId),
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
    enabled: !!quotationId && !!localStorage.getItem('access_token'), // Only run if ID is provided and user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useCustomerQuotations = (customerId, page = 1, pageSize = 10, status = null, search = null, dateFrom = null, dateTo = null, type = null, sortTypes = []) => {
  return useQuery({
    queryKey: [...queryKeys.customerQuotations(customerId), page, pageSize, status, search, dateFrom, dateTo, type, sortTypes],
    queryFn: () => authenticatedApi.getCustomerQuotations(customerId, page, pageSize, status, search, dateFrom, dateTo, type, sortTypes),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!customerId && !!localStorage.getItem('access_token'), // Only run if ID is provided and user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useAllQuotations = (page = 1, pageSize = 10, filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.allQuotations(), page, pageSize, filters],
    queryFn: () => authenticatedApi.getAllQuotations(page, pageSize, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!localStorage.getItem('access_token'), // Only run if user is authenticated
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useRolesByUserType = (userType, options = {}) => {
  return useQuery({
    queryKey: queryKeys.rolesByUserType(userType),
    queryFn: () => publicApi.getRolesByUserType(userType),
    enabled: !!userType,
    staleTime: 10 * 60 * 1000, // 10 minutes - roles don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    ...options, // Allow passing additional options like enabled
  });
};



// Mutation hooks (authenticated)
export const useLogin = () => {
  return useMutation({
    mutationFn: ({ credentials, language }) => publicApi.login(credentials, language),
    onError: (error) => {
      console.error('Failed to login:', error);
    },
  });
};

export const useSendMobileOtp = () => {
  return useMutation({
    mutationFn: ({ phoneNumber, language }) => publicApi.sendMobileOtp(phoneNumber, language),
    onError: (error) => {
      console.error('Failed to send mobile OTP:', error);
    },
  });
};

export const useSendEmailOtp = () => {
  return useMutation({
    mutationFn: ({ email, language }) => publicApi.sendEmailOtp(email, language),
    onError: (error) => {
      console.error('Failed to send email OTP:', error);
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: ({ email, language }) => publicApi.forgotPassword(email, language),
    onError: (error) => {
      console.error('Failed to send forgot password email:', error);
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ token, email, password, confirmPassword, language }) => publicApi.resetPassword(token, email, password, confirmPassword, language),
    onError: (error) => {
      console.error('Failed to reset password:', error);
    },
  });
};

export const useVerifyEmailOtp = () => {
  return useMutation({
    mutationFn: ({ email, otp, language }) => publicApi.verifyEmailOtp(email, otp, language),
    onError: (error) => {
      console.error('Failed to verify email OTP:', error);
    },
  });
};

export const useSecureVerifyEmailOtp = () => {
  return useMutation({
    mutationFn: ({ key, otp, language }) => publicApi.secureVerifyEmailOtp(key, otp, language),
    onError: (error) => {
      console.error('Failed to verify email OTP:', error);
    },
  });
};

export const useVerifyMobileOtp = () => {
  return useMutation({
    mutationFn: ({ phoneNumber, otp, language }) => publicApi.verifyMobileOtp(phoneNumber, otp, language),
    onError: (error) => {
      console.error('Failed to verify mobile OTP:', error);
    },
  });
};

export const useSecureVerifyMobileOtp = () => {
  return useMutation({
    mutationFn: ({ key, otp, language }) => publicApi.secureVerifyMobileOtp(key, otp, language),
    onError: (error) => {
      console.error('Failed to verify mobile OTP:', error);
    },
  });
};

export const useRegisterProvider = () => {
  return useMutation({
    mutationFn: ({ userData, language }) => publicApi.registerProvider(userData, language),
    onError: (error) => {
      console.error('Failed to register provider:', error);
    },
  });
};

export const useVerifyOtpSequentially = () => {
  return useMutation({
    mutationFn: ({ phoneNumber, mobileOtp, email, emailOtp }) => 
      publicApi.verifyOtpSequentially(phoneNumber, mobileOtp, email, emailOtp),
    onError: (error) => {
      console.error('Failed to verify OTP sequentially:', error);
    },
  });
};


export const useApproveContractor = () => {
  const queryClient = useQueryClient(); // to refresh data after approval if needed
  
  return useMutation({
    mutationFn: ({ userId, approvalStatus, rejectedReason }) =>
      authenticatedApi.approveContractor(userId, approvalStatus, rejectedReason),
    onSuccess: (data, variables) => {
      // Refresh ALL contractor queries (with any parameters) and contractor details after approval
      queryClient.invalidateQueries({
        queryKey: ['contractors'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.contractorDetails(variables.userId),
      });
      
      // Force refetch all contractor-related queries to ensure fresh data
      queryClient.refetchQueries({
        queryKey: ['contractors'],
      });
    },
    onError: (error, variables) => {
      console.error('Failed to update contractor approval status:', error);
      console.error('Variables:', variables);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useApproveLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceProviderId, status, RejectionReason }) =>
      authenticatedApi.approveLicense(
        serviceProviderId,
        status,
        RejectionReason
      ),

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['contractors'],
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.contractorDetails(variables.serviceProviderId),
      });

      queryClient.refetchQueries({
        queryKey: ['contractors'],
      });
    },

    onError: (error, variables) => {
      console.error('Failed to update license approval status:', error);
      console.error('Variables:', variables);

      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};


// Authenticated Dashboard API hooks
export const useDashboardStats = () => {
  return useQuery({
    queryKey: queryKeys.dashboardStats,
    queryFn: () => authenticatedApi.getDashboardStats(),
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    retry: 2,
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Authenticated Quotations API hooks
export const useQuotations = (statuses = ['CREATED', 'DECLINED', 'INPROGRESS'], page = 1, pageSize = 5, filters = {}, sortTypes = []) => {
  return useQuery({
    queryKey: [...queryKeys.quotations, statuses, page, pageSize, filters, sortTypes],
    queryFn: async () => {
      try {
        const response = await authenticatedApi.getQuotations(statuses, page, pageSize, { ...filters, sortTypes });
        return response;
      } catch (error) {
        console.error('useQuotations hook - Error:', error);
        throw error;
      }
    },
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Public Services API hooks
export const useServices = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.services,
    queryFn: () => publicApi.getServices(),
    staleTime: 10 * 60 * 1000, // 10 min
    gcTime: 30 * 60 * 1000,   // 30 min
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options, // Allow passing additional options like enabled
  });
};

// Submit Quotation mutation hook
export const useSubmitQuotation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (quotationData) => authenticatedApi.submitQuotation(quotationData),
    onSuccess: (data, variables) => {
      // Refresh quotations list after successful submission
      queryClient.invalidateQueries({
        queryKey: queryKeys.quotations,
      });
    },
    onError: (error, variables) => {
      console.error('Failed to submit quotation:', error);
      console.error('Variables:', variables);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Change Quotation Status mutation hook
export const useChangeQuotationStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ quotationId, status, rejectedReason }) => {
      const payload = { quotationId, status };
      if (rejectedReason) {
        payload.rejectedReason = rejectedReason;
      }
      return authenticatedApi.changeQuotationStatus(payload);
    },
    onSuccess: (data, variables) => {
      // Refresh quotations list after status change
      queryClient.invalidateQueries({
        queryKey: queryKeys.quotations,
      });
    },
    onError: (error, variables) => {
      console.error('Failed to change quotation status:', error);
      console.error('Variables:', variables);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Update User Availability mutation hook
export const useUpdateAvailability = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (isAvailable) => authenticatedApi.updateAvailability(isAvailable),
    onSuccess: (data, variables) => {
      // Refresh dashboard stats after availability update
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats,
      });
    },
    onError: (error, variables) => {
      console.error('Failed to update availability:', error);
      console.error('Variables:', variables);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Get Quotation Overview query hook
export const useQuotationOverview = (quotationId) => {
  return useQuery({
    queryKey: queryKeys.quotationOverview(quotationId),
    queryFn: () => authenticatedApi.getQuotationOverview(quotationId),
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    enabled: !!quotationId && !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Get Quotation Detail query hook
export const useQuotationDetail = (quotationId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quotationDetail(quotationId),
    queryFn: () => {
      return authenticatedApi.getQuotationDetail(quotationId);
    },
    staleTime: 0, // No caching
    gcTime: 0, // No caching
    enabled: options.enabled !== undefined ? options.enabled : (!!quotationId && !!localStorage.getItem('access_token')),
    onSuccess: (data) => {
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Get User Profile query hook
export const useUserProfile = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.userProfile,
    queryFn: () => authenticatedApi.getUserProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options, // Allow passing additional options like enabled
  });
};

// Get Notifications query hook
export const useNotifications = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => authenticatedApi.getAllNotifications(),
    staleTime: 30 * 1000, // 30 seconds - notifications should be relatively fresh
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options, // Allow passing additional options like enabled
  });
};

// Get Unread Count query hook
export const useUnreadCount = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.unreadCount,
    queryFn: () => authenticatedApi.getUnreadCount(),
    staleTime: 10 * 1000, // 10 seconds - count should be very fresh
    gcTime: 30 * 1000, // 30 seconds
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options, // Allow passing additional options like enabled
  });
};

// Mark Notifications as Read mutation hook
export const useMarkNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (notificationIds) => authenticatedApi.setReadAll(notificationIds),
    onSuccess: (data, variables) => {
      // Invalidate and refetch notifications and unread count
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.unreadCount,
      });
    },
    onError: (error, variables) => {
      console.error('Failed to mark notifications as read:', error);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// Offers API hooks
export const useOffers = (searchTerm = null, filter = null, page = 1, pageSize = 10, language = null) => {
  return useQuery({
    queryKey: queryKeys.offers(searchTerm, filter, page, pageSize, language),
    queryFn: () => authenticatedApi.getOffers(searchTerm, filter, page, pageSize, language),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useOffer = (id, options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.offers, id],
    queryFn: () => authenticatedApi.getOffer(id),
    enabled: !!id && !!localStorage.getItem('access_token') && (options.enabled !== false),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
    ...options,
  });
};

export const useCreateOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ offerData, language = null }) => authenticatedApi.createOffer(offerData, language),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['offers'],
      });
    },
    onError: (error) => {
      console.error('Failed to create offer:', error);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, offerData, language = null }) => authenticatedApi.updateOffer(id, offerData, language),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['offers'],
      });
    },
    onError: (error) => {
      console.error('Failed to update offer:', error);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.deleteOffer(id, language),
    onSuccess: () => {
      // Invalidate all offers queries (regardless of searchTerm/filter parameters)
      queryClient.invalidateQueries({
        queryKey: ['offers'],
      });
    },
    onError: (error) => {
      console.error('Failed to delete offer:', error);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useReorderOffers = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ offerIds, language = null }) => authenticatedApi.reorderOffers(offerIds, language),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['offers'],
      });
    },
    onError: (error) => {
      console.error('Failed to reorder offers:', error);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useUpdateOfferStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status, offerData, language = null }) =>
      authenticatedApi.updateOfferStatus(id, status, offerData, language),
    onSuccess: () => {
      // Invalidate all offers queries (regardless of searchTerm/filter parameters)
      queryClient.invalidateQueries({
        queryKey: ['offers'],
      });
    },
    onError: (error) => {
      console.error('Failed to update offer status:', error);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useSaveOfferOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ offers, language = null }) => authenticatedApi.saveOfferOrder(offers, language),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['offers'],
      });
    },
    onError: (error) => {
      console.error('Failed to save offer order:', error);
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

// ── Supplier Materials hooks ──

export const useSupplierMaterials = () => {
  return useQuery({
    queryKey: ['supplierMaterials'],
    queryFn: () => authenticatedApi.getSupplierMaterials(),
    select: (data) => data?.result ?? data ?? [],
    staleTime: 5 * 60 * 1000,
    enabled: !!localStorage.getItem('access_token'),
  });
};

export const useUpdateSupplierMaterials = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => authenticatedApi.updateSupplierMaterials(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplierMaterials'] });
    },
  });
};

// ── Material Master hooks ──

export const useMaterialHierarchy = () => {
  return useQuery({
    queryKey: queryKeys.materialHierarchy,
    queryFn: async () => {
      const response = await authenticatedApi.getMaterialHierarchy();
      return response?.result || response;
    },
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    enabled: !!localStorage.getItem('access_token'),
    onError: (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
    },
  });
};

export const useGetMaterial = (materialId) => {
  return useQuery({
    queryKey: ['material', materialId],
    queryFn: async () => {
      const response = await authenticatedApi.getMaterial(materialId);
      return response?.result || response;
    },
    staleTime: 0,
    enabled: !!localStorage.getItem('access_token') && !!materialId,
  });
};

export const useGetSubCategory = (subCategoryId) => {
  return useQuery({
    queryKey: ['subCategory', subCategoryId],
    queryFn: async () => {
      const response = await authenticatedApi.getSubCategoryById(subCategoryId);
      return response?.result || response;
    },
    staleTime: 0,
    enabled: !!localStorage.getItem('access_token') && !!subCategoryId,
  });
};

export const useMainCategories = () => {
  return useQuery({
    queryKey: ['mainCategories'],
    queryFn: async () => {
      const response = await authenticatedApi.getMainCategories();
      return response?.result || response;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!localStorage.getItem('access_token'),
  });
};

export const useSubCategories = (mainCategoryId) => {
  return useQuery({
    queryKey: ['subCategories', mainCategoryId],
    queryFn: async () => {
      const response = await authenticatedApi.getSubCategories(mainCategoryId);
      return response?.result || response;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!localStorage.getItem('access_token') && !!mainCategoryId,
  });
};

const materialMutationOptions = (queryClient) => ({
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['materialHierarchy'] });
    queryClient.invalidateQueries({ queryKey: ['material'] });
  },
  onError: (error) => {
    if (error?.response?.status === 401) handleUnauthorized();
  },
});

export const useCreateMainCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, language = null }) => authenticatedApi.createMainCategory(data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useUpdateMainCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, language = null }) => authenticatedApi.updateMainCategory(id, data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useDeleteMainCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.deleteMainCategory(id, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useCreateSubCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, language = null }) => authenticatedApi.createSubCategory(data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useUpdateSubCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, language = null }) => authenticatedApi.updateSubCategory(id, data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useDeleteSubCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.deleteSubCategory(id, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useCreateMaterial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, language = null }) => authenticatedApi.createMaterial(data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useUpdateMaterial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, language = null }) => authenticatedApi.updateMaterial(id, data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useDeleteMaterial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.deleteMaterial(id, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useArchiveMainCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.archiveMainCategory(id, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useArchiveSubCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.archiveSubCategory(id, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useArchiveMaterial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.archiveMaterial(id, language),
    ...materialMutationOptions(queryClient),
  });
};

// --- Material Sizes hooks ---
export const useCreateMaterialSize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ materialId, data, language = null }) => authenticatedApi.createMaterialSize(materialId, data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useUpdateMaterialSize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sizeId, data, language = null }) => authenticatedApi.updateMaterialSize(sizeId, data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useDeleteMaterialSize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sizeId, language = null }) => authenticatedApi.deleteMaterialSize(sizeId, language),
    ...materialMutationOptions(queryClient),
  });
};

// --- Material Brands hooks ---
export const useAddMaterialBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ materialId, data, language = null }) => authenticatedApi.addMaterialBrand(materialId, data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useUpdateMaterialBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ materialBrandId, data, language = null }) => authenticatedApi.updateMaterialBrand(materialBrandId, data, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useDeleteMaterialBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ materialBrandId, language = null }) => authenticatedApi.deleteMaterialBrand(materialBrandId, language),
    ...materialMutationOptions(queryClient),
  });
};

// --- Image removal hooks ---
export const useRemoveMainCategoryImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.removeMainCategoryImage(id, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useRemoveSubCategoryImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.removeSubCategoryImage(id, language),
    ...materialMutationOptions(queryClient),
  });
};

export const useRemoveMaterialImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, language = null }) => authenticatedApi.removeMaterialImage(id, language),
    ...materialMutationOptions(queryClient),
  });
};

// ─── Supplier / Contractor Portal ─────────────────────────────────────────────

export const supplierKeys = {
  project: (id) => ["supplier", "project", id],
  // GET /Property/get returns all user properties — key is user-scoped, not project-scoped
  projectAddresses: () => ["supplier", "properties"],
  projectMedia: (projectId) => ["supplier", "media", projectId],
  projectRFQs: (projectId) => ["supplier", "rfqs", projectId],
  categories: () => ["supplier", "categories"],
  materialItems: (categoryId) => ["supplier", "materials", categoryId],
  materialSubCategories: (language) => ["metadata", "material-sub-categories", language],
  suppliers: (filters) => ["supplier", "list", filters],
  requests: (filters) => ["supplier", "requests", filters],
  requestDetails: (id) => ["supplier", "request-details", id],
  requestStatuses: () => ["metadata", "quotation-request-statuses"],
  requestSortOptions: () => ["metadata", "supplier-request-sort-by"],
  bookingMaterialDetails: (id) => ["supplier", "booking-material", id],
  bookingMaterialQuotation: (bookingId, quotationId) => ["supplier", "booking-material-quotation", bookingId, quotationId],
  processDetail: (id) => ["supplier", "process-detail", id],
};

/** GET /api/Quotation/supplier/requests — paginated & filtered list of quotation requests */
export const useSupplierRequests = ({ page = 1, limit = 20, subCategoryId, status, sortBy } = {}) => {
  const filters = { page, limit, subCategoryId, status, sortBy };
  return useQuery({
    queryKey: supplierKeys.requests(filters),
    queryFn: () => supplierApi.getSupplierRequests(filters),
    select: (data) => {
      const result = data?.result ?? data;
      const totalCount = result?.totalCount ?? result?.TotalCount ?? 0;
      const pageSize = result?.pageSize ?? result?.PageSize ?? limit;
      const items = result?.items ?? result?.Items ?? [];
      return {
        totalCount,
        page: result?.page ?? result?.Page ?? 1,
        pageSize,
        totalPages: pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1,
        items: items.map((item) => ({
          id: `REQ-${item.requestId ?? item.RequestId}`,
          requestId: item.requestId ?? item.RequestId,
          requestNumber: item.requestNumber ?? item.RequestNumber,
          projectName: item.propertyName ?? item.PropertyName,
          image: item.propertyLogoUrl ?? item.PropertyLogoUrl ?? null,
          location: [item.city ?? item.City, item.location ?? item.Location].filter(Boolean).join(', '),
          materials: item.subCategoryNames ?? item.SubCategoryNames ?? [],
          requestedDate: item.bookingDateTime ?? item.BookingDateTime,
          status: item.status ?? item.Status,
        })),
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

/** GET /api/MetaData/quotation-request-statuses — list of valid status strings */
export const useQuotationRequestStatuses = () => {
  return useQuery({
    queryKey: supplierKeys.requestStatuses(),
    queryFn: () => supplierApi.getQuotationRequestStatuses(),
    select: (data) => (Array.isArray(data) ? data : data?.result ?? []),
    staleTime: 60 * 60 * 1000,
  });
};

/** GET /api/MetaData/order-issue-types */
export const useOrderIssueTypes = () => {
  return useQuery({
    queryKey: ['metadata', 'order-issue-types'],
    queryFn: () => supplierApi.getOrderIssueTypes(),
    select: (data) => (Array.isArray(data) ? data : data?.result ?? []),
    staleTime: 60 * 60 * 1000,
  });
};

/** GET /api/MetaData/supplier-quotation-request-sort-by — list of valid sort-by strings */
export const useSupplierRequestSortOptions = () => {
  return useQuery({
    queryKey: supplierKeys.requestSortOptions(),
    queryFn: () => supplierApi.getSupplierRequestSortOptions(),
    select: (data) => (Array.isArray(data) ? data : data?.result ?? []),
    staleTime: 60 * 60 * 1000,
  });
};

/** GET /api/AppHome/materials/sub-categories — flat list of all active sub-categories, language-aware */
export const useMaterialSubCategories = (language = null) => {
  return useQuery({
    queryKey: supplierKeys.materialSubCategories(language),
    queryFn: () => supplierApi.getMaterialSubCategories(language),
    select: (data) => (Array.isArray(data) ? data : data?.result ?? []),
    staleTime: 10 * 60 * 1000,
  });
};

/** GET /api/quotation/supplier/requests/:id — full details for a single request */
export const useSupplierRequestDetails = (id) => {
  return useQuery({
    queryKey: supplierKeys.requestDetails(id),
    queryFn: () => supplierApi.getSupplierRequestDetails(id),
    enabled: !!id,
    select: (data) => {
      const raw = data?.result ?? data;
      const project = raw?.project ?? {};
      const customer = raw?.customer ?? {};
      const booking = raw?.booking ?? {};
      return {
        requestId: booking.requestId,
        bookingRequestId: booking.bookingRequestId,
        id: `REQ-${booking.requestId}`,
        requestNumber: booking.bookingRequestNumber,
        projectName: project.name,
        location: [project.city, project.location].filter(Boolean).join(', '),
        image: null,
        status: booking.status,
        date: booking.createdAt,
        expiryDate: booking.expiryDate,
        requirements: project.requirements,
        customer: {
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
        },
        mediaFiles: (project.mediaFiles ?? []).map((f) => ({
          url: f.url ?? f.mediaFileURL,
          name: f.fileName ?? f.mediaFileName,
          type: (f.fileName ?? f.mediaFileName ?? '')?.match(/\.(pdf)$/i) ? 'pdf' : 'image',
        })),
        materials: (booking.materialGroups ?? []).map((g) => g.subCategory?.name).filter(Boolean),
        // materialGroups passed raw so the UI can access bookingMaterialId / bookingMaterialSizeId
        materialGroups: booking.materialGroups ?? [],
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

/** POST /api/Quotation/supplier/requests/{id}/accept */
export const useAcceptSupplierRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quotationRequestId) => supplierApi.acceptSupplierRequest(quotationRequestId),
    onSuccess: (_, quotationRequestId) => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.requestDetails(String(quotationRequestId)) });
      queryClient.invalidateQueries({ queryKey: supplierKeys.processDetail(String(quotationRequestId)) });
      queryClient.invalidateQueries({ queryKey: ['supplier', 'requests'] });
    },
  });
};

/** POST /api/Quotation/supplier/requests/{id}/reject */
export const useRejectSupplierRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quotationRequestId, reason }) => supplierApi.rejectSupplierRequest(quotationRequestId, reason),
    onSuccess: (_, { quotationRequestId }) => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.requestDetails(String(quotationRequestId)) });
      queryClient.invalidateQueries({ queryKey: supplierKeys.processDetail(String(quotationRequestId)) });
      queryClient.invalidateQueries({ queryKey: ['supplier', 'requests'] });
    },
  });
};

/** POST /api/Quotation/supplier/requests/{id}/submit */
export const useSubmitSupplierQuotation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quotationRequestId, ...body }) =>
      supplierApi.submitSupplierQuotation(quotationRequestId, body),
    onSuccess: (_, { quotationRequestId }) => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.requestDetails(String(quotationRequestId)) });
      queryClient.invalidateQueries({ queryKey: supplierKeys.processDetail(String(quotationRequestId)) });
      queryClient.invalidateQueries({ queryKey: ['contractor', 'quotation-history', String(quotationRequestId)] });
      queryClient.invalidateQueries({ queryKey: ['supplier', 'requests'] });
    },
  });
};

/** GET /api/Quotation/supplier/requests/{id}/process-detail */
export const useQuotationProcessDetail = (quotationRequestId, queryOptions = {}) => {
  const { enabled: enabledOverride, ...rest } = queryOptions;
  const baseEnabled = !!quotationRequestId;
  const enabled =
    typeof enabledOverride === 'boolean' ? baseEnabled && enabledOverride : baseEnabled;
  return useQuery({
    queryKey: supplierKeys.processDetail(quotationRequestId),
    queryFn: () => supplierApi.getQuotationProcessDetail(quotationRequestId),
    enabled,
    staleTime: 2 * 60 * 1000,
    ...rest,
  });
};

/**
 * List contractor projects with optional client-side text search.
 * Shares the ["supplier", "properties"] cache with useProjectAddresses.
 */
export const useSupplierProjects = (search = "") => {
  return useQuery({
    queryKey: supplierKeys.projectAddresses(),
    queryFn: () => supplierApi.getProjectAddresses(),
    select: (data) => {
      const projects = data.result ?? [];
      if (!search.trim()) return projects;
      const q = search.toLowerCase().trim();
      return projects.filter(
        (p) => p.name.toLowerCase().includes(q) || p.location.toLowerCase().includes(q)
      );
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** Single project detail. */
export const useSupplierProject = (projectId) => {
  return useQuery({
    queryKey: supplierKeys.project(projectId),
    queryFn: () => supplierApi.getProjectById(projectId),
    select: (data) => data.result,
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

/** GET /api/Booking/property-materials/{bookingId} */
export const useBookingMaterialDetails = (bookingId) => {
  return useQuery({
    queryKey: supplierKeys.bookingMaterialDetails(bookingId),
    queryFn: () => supplierApi.getBookingMaterialDetails(bookingId),
    enabled: !!bookingId,
    staleTime: 5 * 60 * 1000,
  });
};

/** GET /api/Booking/quotation-requests/{quotationRequestId}/detail */
export const useContractorQuotationRequestDetail = (quotationRequestId) => {
  return useQuery({
    queryKey: ["contractor", "quotation-request-detail", quotationRequestId],
    queryFn: () => supplierApi.getContractorQuotationRequestDetail(quotationRequestId),
    enabled: !!quotationRequestId,
    staleTime: 2 * 60 * 1000,
  });
};

/** POST /api/Quotation/supplier/requests/{quotationRequestId}/accept */
export const useAcceptQuotationRequest = (quotationRequestId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => supplierApi.acceptQuotationRequest(quotationRequestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contractor", "quotation-request-detail", quotationRequestId] });
    },
  });
};

/** POST /api/Quotation/supplier/requests/{quotationRequestId}/reject */
export const useRejectQuotationRequest = (quotationRequestId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason) => supplierApi.rejectQuotationRequest(quotationRequestId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contractor", "quotation-request-detail", quotationRequestId] });
    },
  });
};

/** POST /api/quotation/material-requests/{quotationRequestId}/negotiate */
export const useNegotiateQuotationRequest = (quotationRequestId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => supplierApi.negotiateQuotationRequest(quotationRequestId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contractor", "quotation-request-detail", quotationRequestId] });
    },
  });
};

/** GET /api/Quotation/material-requests/{quotationRequestId}/history */
export const useQuotationHistory = (quotationRequestId, enabled = false) => {
  return useQuery({
    queryKey: ['contractor', 'quotation-history', quotationRequestId],
    queryFn: () => supplierApi.getQuotationHistory(quotationRequestId),
    enabled: !!quotationRequestId && enabled,
    staleTime: 2 * 60 * 1000,
  });
};

/** GET /api/Payment/providers */
export const usePaymentProviders = (enabled = true) => {
  return useQuery({
    queryKey: ['payment', 'providers'],
    queryFn: () => supplierApi.getPaymentProviders(),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
};

/** POST /api/Quotation/material-requests/{quotationRequestId}/checkout?provider= */
export const useMaterialRequestCheckout = (quotationRequestId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider) => supplierApi.checkoutMaterialQuotationRequest(quotationRequestId, provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractor', 'quotation-request-detail', quotationRequestId] });
    },
  });
};

/** GET /api/Booking/property-materials/{bookingId}/quotations/{supplierQuotationId} */
export const useBookingMaterialQuotationDetails = (bookingId, quotationId) => {
  return useQuery({
    queryKey: supplierKeys.bookingMaterialQuotation(bookingId, quotationId),
    queryFn: () => supplierApi.getBookingMaterialQuotationDetails(bookingId, quotationId),
    enabled: !!bookingId && !!quotationId,
    staleTime: 5 * 60 * 1000,
  });
};

/** POST /api/Order/{orderId}/accept */
export const useAcceptOrder = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => supplierApi.acceptOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/** POST /api/Order/{orderId}/confirm */
export const useConfirmOrderDelivery = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => supplierApi.confirmOrderDelivery(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/** POST /api/Order/{orderId}/refund */
export const useRefundOrder = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason) => supplierApi.refundOrder(orderId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/** POST /api/Order/{orderId}/cancel */
export const useCancelOrder = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason) => supplierApi.cancelOrder(orderId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/** POST /api/Order/{orderId}/status/in-progress */
export const useMarkOrderInProgress = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => supplierApi.markOrderInProgress(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/** POST /api/Order/{orderId}/status/out-for-delivery */
export const useMarkOrderOutForDelivery = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryInfo) => supplierApi.markOrderOutForDelivery(orderId, deliveryInfo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/** POST /api/Order/{orderId}/status/delivered */
export const useMarkOrderDelivered = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => supplierApi.markOrderDelivered(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
};

/** POST /api/Order/{orderId}/delivery-proof */
export const useUploadOrderDeliveryProof = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (proofURL) => supplierApi.uploadOrderDeliveryProof(orderId, proofURL),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    },
  });
};

/** PUT /api/Order/{orderId}/delivery-info */
export const useUpdateOrderDeliveryInfo = (orderId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryInfo) => supplierApi.updateOrderDeliveryInfo(orderId, deliveryInfo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    },
  });
};

/** GET /api/Order */
export const useOrders = (params = {}) => {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => supplierApi.getOrders(params),
    staleTime: 2 * 60 * 1000,
  });
};

/** GET /api/Order/{orderId} */
export const useOrderDetail = (orderId) => {
  return useQuery({
    queryKey: ['order', orderId],
    queryFn: () => supplierApi.getOrderDetail(orderId),
    enabled: !!orderId,
    staleTime: 2 * 60 * 1000,
  });
};

/** GET /api/admin/orders */
export const useAdminOrders = (params = {}) => {
  return useQuery({
    queryKey: ['admin-orders', params],
    queryFn: () => supplierApi.getAdminOrders(params),
    staleTime: 2 * 60 * 1000,
  });
};

/** GET /api/admin/orders/{orderId} */
export const useAdminOrderDetail = (orderId) => {
  return useQuery({
    queryKey: ['admin-order', orderId],
    queryFn: () => supplierApi.getAdminOrderDetail(orderId),
    enabled: !!orderId,
    staleTime: 2 * 60 * 1000,
  });
};

/** GET /api/Order/{orderId}/invoice */
export const useOrderInvoice = () => {
  return useMutation({
    mutationFn: (orderId) => supplierApi.getOrderInvoice(orderId),
  });
};

/** POST /api/Order/{orderId}/issue */
export const useRaiseOrderIssue = (orderId) => {
  return useMutation({
    mutationFn: (payload) => supplierApi.raiseOrderIssue(orderId, payload),
  });
};

/** GET /api/Order/{orderId}/issues */
export const useOrderIssues = (orderId) => {
  return useQuery({
    queryKey: ['order-issues', orderId],
    queryFn: () => supplierApi.getOrderIssues(orderId),
    select: (data) => (Array.isArray(data) ? data : data?.result ?? []),
    enabled: !!orderId,
    staleTime: 30 * 1000,
  });
};

/**
 * GET /Property/get
 * Returns all properties for the authenticated user.
 * Used as the address dropdown in Step 3.
 */
export const useProjectAddresses = () => {
  return useQuery({
    queryKey: supplierKeys.projectAddresses(),
    queryFn: () => supplierApi.getProjectAddresses(),
    select: (data) => data.result,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * POST /Property/create
 * Creates a new property. Invalidates the properties list on success.
 */
export const useSaveProjectAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData) => supplierApi.createProjectAddress(null, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.projectAddresses() });
    },
  });
};

/** Media files attached to a project. */
export const useProjectMedia = (projectId) => {
  return useQuery({
    queryKey: supplierKeys.projectMedia(projectId),
    queryFn: () => supplierApi.getProjectMedia(projectId),
    select: (data) => data.result,
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

/** RFQs submitted for a project. */
export const useProjectRFQs = (projectId) => {
  return useQuery({
    queryKey: supplierKeys.projectRFQs(projectId),
    queryFn: () => supplierApi.getProjectRFQs(projectId),
    select: (data) => data.result,
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  });
};

/** All material categories via /AppHome/materials/hierarchy — LIVE. */
export const useMaterialCategories = (language = null, searchKey = null) => {
  return useQuery({
    queryKey: [...supplierKeys.categories(), language, searchKey],
    queryFn: () => supplierApi.getMaterialCategories(language, searchKey),
    select: (raw) => {
      const hierarchy = raw?.result || raw || [];
      const groups = hierarchy.map((main) => ({
        id: main.id,
        name: main.name,
        subcategories: (main.subCategories || []).map((sub) => ({
          id: sub.id,
          name: sub.name,
          image: sub.imageUrl || '',
        })),
      }));

      const brandMap = new Map();
      hierarchy.forEach((main) => {
        (main.subCategories || []).forEach((sub) => {
          (sub.materials || []).forEach((mat) => {
            (mat.brands || []).forEach((b) => {
              if (b.brandName && !brandMap.has(b.brandName)) {
                brandMap.set(b.brandName, { name: b.brandName, image: b.brandImageUrl || '' });
              }
            });
          });
        });
      });

      return { groups, standalone: [], brands: [...brandMap.values()] };
    },
    staleTime: 10 * 60 * 1000,
  });
};

/** Material items for a given subcategory — extracted from the hierarchy cache. */
export const useMaterialItems = (subCategoryId, language = null) => {
  return useQuery({
    queryKey: [...supplierKeys.categories(), language, null],
    queryFn: () => supplierApi.getMaterialCategories(language, null),
    select: (raw) => {
      const hierarchy = raw?.result || raw || [];
      for (const main of hierarchy) {
        const sub = (main.subCategories || []).find((s) => s.id === subCategoryId);
        if (sub) {
          return (sub.materials || []).map((mat) => {
            const sizes = mat.sizes || [];
            const hasSizes = sizes.length > 1;
            return {
              id: mat.id,
              name: mat.name,
              type: hasSizes ? 'multiSize' : 'simple',
              unit: sizes[0]?.unitType || '',
              availableSizes: hasSizes
                ? sizes.map((s) => ({ id: s.materialSizeId ?? s.suppliedMaterialSizeId ?? s.id ?? 0, value: s.size, unit: s.unitType || '' }))
                : undefined,
              fixedSize: !hasSizes && sizes[0]?.size ? sizes[0].size : undefined,
              fixedSizeId: !hasSizes ? (sizes[0]?.materialSizeId ?? sizes[0]?.suppliedMaterialSizeId ?? sizes[0]?.id ?? 0) : null,
              brands: (mat.brands || []).map((b) => ({
                id: b.materialBrandId ?? b.suppliedMaterialBrandId ?? b.id ?? b.brandId ?? 0,
                name: b.brandName,
                image: b.brandImageUrl || '',
              })),
            };
          });
        }
      }
      return [];
    },
    enabled: !!subCategoryId,
    staleTime: 10 * 60 * 1000,
  });
};

/** All material items across every subcategory — same shape as useMaterialItems but not filtered. */
export const useAllMaterialItems = (language = null) => {
  return useQuery({
    queryKey: [...supplierKeys.categories(), language, null],
    queryFn: () => supplierApi.getMaterialCategories(language, null),
    select: (raw) => {
      const hierarchy = raw?.result || raw || [];
      const all = [];
      hierarchy.forEach((main) => {
        (main.subCategories || []).forEach((sub) => {
          (sub.materials || []).forEach((mat) => {
            const sizes = mat.sizes || [];
            const hasSizes = sizes.length > 1;
            all.push({
              id: mat.id,
              subcategoryId: sub.id,
              name: mat.name,
              type: hasSizes ? 'multiSize' : 'simple',
              unit: sizes[0]?.unitType || '',
              availableSizes: hasSizes
                ? sizes.map((s) => ({ id: s.materialSizeId ?? s.suppliedMaterialSizeId ?? s.id ?? 0, value: s.size, unit: s.unitType || '' }))
                : undefined,
              fixedSize: !hasSizes && sizes[0]?.size ? sizes[0].size : undefined,
              fixedSizeId: !hasSizes ? (sizes[0]?.materialSizeId ?? sizes[0]?.suppliedMaterialSizeId ?? sizes[0]?.id ?? 0) : null,
              brands: (mat.brands || []).map((b) => ({
                id: b.materialBrandId ?? b.suppliedMaterialBrandId ?? b.id ?? b.brandId ?? 0,
                name: b.brandName,
                image: b.brandImageUrl || '',
              })),
            });
          });
        });
      });
      return all;
    },
    staleTime: 10 * 60 * 1000,
  });
};

/** Map of materialId → name in the current language, derived from the shared categories cache. */
export const useMaterialNameMap = (language = null) => {
  return useQuery({
    queryKey: [...supplierKeys.categories(), language],
    queryFn: () => supplierApi.getMaterialCategories(language),
    select: (raw) => {
      const hierarchy = raw?.result || raw || [];
      const map = {};
      hierarchy.forEach((main) => {
        (main.subCategories || []).forEach((sub) => {
          (sub.materials || []).forEach((mat) => {
            if (mat.id != null) map[mat.id] = mat.name;
          });
        });
      });
      return map;
    },
    staleTime: 10 * 60 * 1000,
  });
};

/** List suppliers with optional search / sort / location / availability filter. */
export const useSuppliers = ({ search = "", sort = "", locationId = "", availability = "" } = {}) => {
  const apiFilters = { search, sort };
  return useQuery({
    queryKey: supplierKeys.suppliers({ ...apiFilters, locationId, availability }),
    queryFn: () => supplierApi.getSuppliers(apiFilters),
    select: (data) => {
      let mapped = (data.result ?? []).map((sp) => {
        const words = (sp.name || '').trim().split(/\s+/).filter(Boolean);
        const initials = words.slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
        return {
          id: sp.id,
          name: sp.name || '',
          rating: sp.averageRating ?? 0,
          location: sp.location?.name || '',
          locationId: sp.location?.id ?? null,
          type: sp.roleLabels?.[0] || sp.roles?.[0] || 'Supplier',
          initials,
          logoUrl: sp.companyLogoUrl || sp.profileUrl || '',
          backgroundColor: sp.backgroundColor || '',
          socialInstagramUrl: sp.socialInstagramUrl || '',
          isFavourite: sp.isFavourite ?? false,
          isAvailable: sp.isAvailable ?? true,
          isVerified: sp.isVerified ?? false,
        };
      });
      if (locationId) mapped = mapped.filter((sp) => String(sp.locationId) === String(locationId));
      if (availability === 'available') mapped = mapped.filter((sp) => sp.isAvailable);
      if (availability === 'busy') mapped = mapped.filter((sp) => !sp.isAvailable);
      return mapped;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** POST /api/Booking/property-materials — Create a material booking. */
export const useCreateMaterialBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => supplierApi.createMaterialBooking(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.projectAddresses() });
    },
  });
};

/** Submit a new RFQ. Invalidates the project's RFQ list on success. */
export const useSubmitRFQ = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => supplierApi.submitRFQ(payload),
    onSuccess: (data, variables) => {
      if (variables?.projectId) {
        queryClient.invalidateQueries({ queryKey: supplierKeys.projectRFQs(variables.projectId) });
      }
    },
  });
};

/** Upload a single file attachment. Returns { id, name, url }. */
export const useUploadFile = () => {
  return useMutation({
    mutationFn: (file) => supplierApi.uploadFile(file),
  });
};


 
